<script lang="ts">
import PaymentCardIcon from '@/components/icons/PaymentCardIcon.vue'
import { useCurrencyFormatter } from '@/composables/useCurrencyFormatter'
import type { Deposit, PaymentMethod } from '@/types'
import { Icons } from '@/utils/helper-objects'
import { type PropType, defineComponent } from 'vue'

export default defineComponent({
  name: 'PaymentMethodsPanel',
  components: { PaymentCardIcon },
  props: {
    paymentErrors: {
      type: String,
      default: null,
    },
    isPaymentReady: {
      type: Boolean,
      default: false,
    },
    isPaymentProcessing: {
      type: Boolean,
      default: false,
    },
    isAddNewCardOpen: {
      type: Boolean,
      default: false,
    },
    isOneClickPayOpen: {
      type: Boolean,
      default: false,
    },
    selectedDeposit: {
      type: Object as PropType<Deposit>,
      required: true,
    },
    paymentForm: {
      type: Object,
      required: true,
    },
    apmConfig: {
      type: Object,
      required: true,
    },
    oneClickPay: {
      type: Object as PropType<PaymentMethod | null>,
      default: null,
    },
    showAddNewCard: {
      type: Boolean,
      default: false,
    },
    successBonusPence: {
      type: Number,
      default: 0,
    },
  },
  emits: [
    'update:isAddNewCardOpen',
    'update:isOneClickPayOpen',
    'clearPaymentErrors',
    'paymentAuthorized',
    'paymentError',
    'paymentProcessing',
    'paymentIntentUpdated',
  ],
  setup() {
    const currencyFormatter = useCurrencyFormatter()
    return {
      formatCurrencyFromPence: currencyFormatter.formatCurrencyFromPence,
    }
  },
  data() {
    return {
      Icons,
    }
  },
  methods: {
    toggleAddNewCard() {
      this.$emit('update:isAddNewCardOpen', !this.isAddNewCardOpen)
    },
    toggleOneClickPay() {
      this.$emit('update:isOneClickPayOpen', !this.isOneClickPayOpen)
    },
    handlePaymentAuthorized(payment: any) {
      this.$emit('paymentAuthorized', payment)
    },
    handlePaymentError(message: string) {
      this.$emit('paymentError', message)
    },
    handlePaymentProcessing(isProcessing: boolean) {
      this.$emit('paymentProcessing', isProcessing)
    },
    handlePaymentIntentUpdated(intent: any) {
      this.$emit('paymentIntentUpdated', intent)
    },
  },
})
</script>

<template>
  <div class="flex flex-col gap-4 mr-2">
    <p v-if="!isOneClickPayOpen" class="font-bold">Choose Payment Method</p>
    <p v-else class="font-bold">1 Click Payment</p>

    <!-- Payment errors -->
    <div
      v-if="paymentErrors"
      class="mb-4 rounded-lg border-l-4 border-red-500 bg-red-50 p-4"
    >
      <div class="flex items-start justify-between">
        <div class="flex items-start">
          <AppIcon
            :icon="Icons.Error"
            class="mr-3 mt-1 text-red-500"
            size="sm"
          />
          <div>
            <h3 class="text-sm font-medium text-red-800">Payment Error</h3>
            <p class="mt-1 text-sm text-red-700" v-html="paymentErrors" />
          </div>
        </div>

        <button
          class="ml-4 inline-flex rounded-md bg-red-50 p-1.5 text-red-400 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-2 focus:ring-offset-red-50"
          @click="$emit('clearPaymentErrors')"
        >
          <AppIcon :icon="Icons.Times" size="xs" />
        </button>
      </div>
    </div>

    <div v-show="!isOneClickPayOpen" class="flex flex-col gap-4">
      <!-- Apple Pay & Google Pay slot (parent provides the divs with refs) -->
      <slot name="apmButtons" />
      <div class="line-through-sides uppercase text-xs leading-[0]">OR</div>
    </div>

    <div
      v-show="isOneClickPayOpen"
      class="flex border-b flex-col border-b-slate-200"
    >
      <table class="w-full">
        <tbody>
          <tr>
            <td class="pb-3 pr-3 text-left">Deposit Amount:</td>
            <td class="pb-3 text-right font-bold">
              {{ formatCurrencyFromPence(selectedDeposit.amount) }}
            </td>
          </tr>
          <tr>
            <td class="pb-3 pr-3 text-left">{{ BUSINESS_NAME }} Credit:</td>
            <td class="pb-3 text-right font-bold">
              {{ formatCurrencyFromPence(successBonusPence) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Card payment button -->
    <AppButton
      v-if="!isOneClickPayOpen"
      title="Pay With Card"
      :class="{
        'border-2 border-primary bg-primary-lighter': isAddNewCardOpen,
        'border m-[1px] border-slate-300': !isAddNewCardOpen,
      }"
      class="button-secondary min-w-full !leading-5 !font-black"
      :disabled="!isPaymentReady || isPaymentProcessing"
      @click="toggleAddNewCard"
    >
      <AppIcon :icon="Icons.Card" />
      {{ oneClickPay ? 'Add New Card' : 'Pay With Card' }} |
      <AppIcon :icon="Icons.Visa" size="md" class="h-auto" />
      <AppIcon :icon="Icons.Mastercard" size="md" class="h-auto" />
    </AppButton>

    <AppButton
      v-if="oneClickPay && !isAddNewCardOpen && !isOneClickPayOpen"
      title="1 Click Pay"
      class="button-primary min-w-full !leading-5 !font-black"
      :disabled="!isPaymentReady || isPaymentProcessing"
      @click="toggleOneClickPay"
    >
      1 Click Pay |<PaymentCardIcon :brand="oneClickPay.brand" /> ••••
      <span class="font-black">{{ oneClickPay.last_4_digits }}</span>
    </AppButton>

    <!-- Payment form slot (parent provides PaymentStageSolidgate with ref) -->
    <slot name="paymentForm" />
  </div>
</template>

<style scoped>
.payment-stage-solidgate {
  :deep(.payment-container) {
    @apply border border-slate-300 bg-primary-lighter p-3 rounded-lg;
  }
}
</style>
